import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { isBefore, parseISO, startOfDay } from 'date-fns'
import { useField, useFormikContext, type FormikHelpers } from 'formik'
import * as React from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Tooltip } from 'react-tooltip'
import { useLatest } from 'react-use'
import * as Api from 'src/api'
import type * as base from 'src/api/base'
import { CloseIcon } from 'src/assets/icons/customIcons/Close'
import { errorMessage, getLecturerDegreeName } from 'src/helpers/fns'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders, type AuthenticatedHeaders } from 'src/hooks/auth/app'
import Swal from 'sweetalert2'

import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { useTheme } from 'src/state/providers/Theme'
import { Button } from 'src/tailwind/components/Button'
import { Modal } from 'src/tailwind/components/Modal'
import Checkbox from 'src/views/components/forms/formik/Checkbox'
import { Form, useFormData } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextareaInput from 'src/views/components/forms/formik/TextareaInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import Loader from 'src/views/components/Loader'
import NoContent from 'src/views/components/NoContent'
import PopoverComponent from 'src/views/components/PopoverComponent'
import Select from 'src/views/components/Select'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'
import { GRID_TWO } from '../../programs/AdministratorProgramPage/constants'
import { FormSection } from '../../programs/AdministratorProgramPage/FormSection'

const DAY_NAMES_KA = ['', 'ორშაბათი', 'სამშაბათი', 'ოთხშაბათი', 'ხუთშაბათი', 'პარასკევი', 'შაბათი', 'კვირა']
const DAY_NAMES_EN = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

function getHourDayName(day: number, locale: string): string {
  const days = locale === 'ka' ? DAY_NAMES_KA : DAY_NAMES_EN
  return days[day] ?? String(day)
}

function formatHour(hour: Api.AdministratorCourseGroupScheduleHour, locale: string): string {
  const dayName = getHourDayName(hour.day, locale)
  return `${hour.name} (${dayName} ${hour.startTime}-${hour.endTime})`
}

function buildScheduleHourSelectOptions(
  hours: readonly Api.AdministratorCourseGroupScheduleHour[] | undefined,
  locale: string,
  selectedHourIds: readonly string[]
): readonly {
  readonly label: string
  readonly value: string
  readonly options?: readonly { readonly label: string; readonly value: string }[]
}[] {
  const allHours = hours ?? []
  if (allHours.length === 0) return []

  const hourById = new Map(allHours.map((hour) => [String(hour.id), hour]))
  const selectedDay = selectedHourIds.length > 0 ? hourById.get(selectedHourIds[0]!)?.day : undefined

  const visibleHours = selectedDay != null ? allHours.filter((hour) => hour.day === selectedDay) : allHours

  if (selectedDay != null) {
    return visibleHours.map((hour) => ({
      value: String(hour.id),
      label: formatHour(hour, locale),
    }))
  }

  const hoursByDay = new Map<number, Api.AdministratorCourseGroupScheduleHour[]>()
  for (const hour of allHours) {
    const dayHours = hoursByDay.get(hour.day) ?? []
    dayHours.push(hour)
    hoursByDay.set(hour.day, dayHours)
  }

  return [...hoursByDay.entries()]
    .sort(([dayA], [dayB]) => dayA - dayB)
    .map(([day, dayHours]) => ({
      label: getHourDayName(day, locale),
      value: '',
      options: dayHours.map((hour) => ({
        value: String(hour.id),
        label: formatHour(hour, locale),
      })),
    }))
}

function isScheduleDatePast(date: string): boolean {
  if (date === '') return false
  return isBefore(startOfDay(parseISO(date)), startOfDay(new Date()))
}

function throwScheduleFormError(message: string): never {
  throw { type: 'ErrorMessage' as const, message }
}

function getScheduleConflictMessages(error: unknown): readonly string[] | null {
  if (error != null && typeof error === 'object' && 'type' in error) {
    if ((error as { type: string }).type === 'ScheduleConflictsDetected') {
      return (error as Api.AdministratorCourseGroupScheduleConflictsDetected).conflicts.flatMap((c) => c.messages)
    }
    if (
      (error as { type: string }).type === 'ErrorMessage' &&
      (error as base.ErrorMessage).message === 'conflicts_detected' &&
      'conflicts' in error &&
      Array.isArray((error as { conflicts: unknown }).conflicts)
    ) {
      return (error as { conflicts: Api.AdministratorCourseGroupScheduleConflictNotice[] }).conflicts.flatMap(
        (c) => c.messages
      )
    }
  }
  return null
}

function rethrowBuildScheduleError(error: unknown, t: ReturnType<typeof useTranslatable>): never {
  const conflictMessages = getScheduleConflictMessages(error)
  if (conflictMessages != null && conflictMessages.length > 0) {
    throw { type: 'ErrorsObject' as const, errors: { general: conflictMessages } }
  }
  if (error instanceof Error && error.message === 'LOCATION_TYPE_REQUIRED') {
    throwScheduleFormError(t(['გთხოვთ აირჩიოთ ლოკაციის ტიპი', 'Please choose location type']))
  }
  if (error instanceof Error && error.message === 'LOCATION_ID_REQUIRED') {
    throwScheduleFormError(t(['გთხოვთ აირჩიოთ ლოკაცია', 'Please choose location']))
  }
  throw error
}

function canModifySchedule(date: string, isMainAdministrator: boolean, canManageCourses: boolean): boolean {
  if (!canManageCourses) return false
  if (!isScheduleDatePast(date)) return true
  return isMainAdministrator
}

export default function AdministratorCourseSchedulePage(): JSX.Element {
  const t = useTranslatable()
  return (
    <SuspenseWrapper title={t('common:schedule')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

interface FilterValues {
  readonly weekId: string
  readonly hourId: string
  readonly lectureType: string
  readonly lectureFormat: string
  readonly searchWord: string
  readonly locationType: string
  readonly buildingId: string
  readonly roomId: string
  readonly locationId: string
}

const DEFAULT_FILTER: FilterValues = {
  weekId: '',
  hourId: '',
  lectureType: '',
  lectureFormat: '',
  searchWord: '',
  locationType: '',
  buildingId: '',
  roomId: '',
  locationId: '',
}

const FILTER_SEARCH_PARAM_KEYS = [
  'weekId',
  'hourId',
  'lectureType',
  'lectureFormat',
  'searchWord',
  'locationType',
  'buildingId',
  'roomId',
  'locationId',
] as const

function filterValuesFromSearchParams(searchParams: URLSearchParams): FilterValues {
  return {
    weekId: searchParams.get('weekId') ?? '',
    hourId: searchParams.get('hourId') ?? '',
    lectureType: searchParams.get('lectureType') ?? '',
    lectureFormat: searchParams.get('lectureFormat') ?? '',
    searchWord: searchParams.get('searchWord') ?? '',
    locationType: searchParams.get('locationType') ?? '',
    buildingId: searchParams.get('buildingId') ?? '',
    roomId: searchParams.get('roomId') ?? '',
    locationId: searchParams.get('locationId') ?? '',
  }
}

function isScheduleFilterActive(searchParams: URLSearchParams): boolean {
  return FILTER_SEARCH_PARAM_KEYS.some((key) => (searchParams.get(key) ?? '') !== '')
}

function buildScheduleListQuery(
  semId: number,
  searchParams: URLSearchParams
): Api.getAdministratorCourseGroupSchedulesParams['query'] {
  const weekId = searchParams.get('weekId') ?? ''
  const hourId = searchParams.get('hourId') ?? ''
  const lectureType = searchParams.get('lectureType') ?? ''
  const lectureFormat = searchParams.get('lectureFormat') ?? ''
  const searchWord = searchParams.get('searchWord') ?? ''
  const locationType = searchParams.get('locationType') ?? ''
  const buildingId = searchParams.get('buildingId') ?? ''
  const roomId = searchParams.get('roomId') ?? ''

  return {
    semId,
    ...(weekId !== '' && { weekId: parseInt(weekId, 10) }),
    ...(hourId !== '' && { hourId: parseInt(hourId, 10) }),
    ...(lectureType !== '' && { lectureType: parseInt(lectureType, 10) }),
    ...(lectureFormat !== '' && { lectureFormat: parseInt(lectureFormat, 10) }),
    ...(searchWord !== '' && { searchWord }),
    ...(locationType !== '' && { locationType: parseInt(locationType, 10) }),
    ...(buildingId !== '' && { buildingId: parseInt(buildingId, 10) }),
    ...(roomId !== '' && { roomId: parseInt(roomId, 10) }),
  }
}

interface ScheduleLecturerEntry {
  readonly userId: string
  readonly profileId: string
  readonly isPaid: boolean
}

interface ScheduleFormValues {
  readonly hourIds: readonly string[]
  readonly learningWeekDates: readonly string[]
  readonly date: string
  readonly lectureTypeId: string
  readonly lectureFormatId: string
  readonly locationType: string
  readonly buildingId: string
  readonly roomId: string
  readonly locationId: string
  readonly lecturers: readonly ScheduleLecturerEntry[]
  readonly info: string
  readonly isHalfHour: boolean
  readonly isSplitLecture: boolean
  readonly massUpdate: boolean
  readonly force: boolean
}

const DEFAULT_SCHEDULE_FORM: ScheduleFormValues = {
  hourIds: [],
  learningWeekDates: [],
  date: '',
  lectureTypeId: '',
  lectureFormatId: '',
  locationType: '1',
  buildingId: '',
  roomId: '',
  locationId: '',
  lecturers: [],
  info: '',
  isHalfHour: false,
  isSplitLecture: false,
  massUpdate: false,
  force: false,
}

function getFirstListItemId(items: readonly { id: number }[] | undefined): string {
  return items?.[0]?.id != null ? String(items[0].id) : ''
}

function createAddScheduleInitialValues(
  lectureTypes: readonly { id: number }[] | undefined,
  formats: readonly { id: number }[] | undefined
): ScheduleFormValues {
  return {
    ...DEFAULT_SCHEDULE_FORM,
    lectureTypeId: getFirstListItemId(lectureTypes),
    lectureFormatId: getFirstListItemId(formats),
  }
}

interface LecturerDisplayItem {
  readonly id: string
  readonly name: string
  readonly personalNumber?: string | null
}

function LecturerDetailsList({ lecturers }: { readonly lecturers: readonly LecturerDisplayItem[] }): JSX.Element {
  return (
    <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {lecturers.map((lecturer) => (
        <li key={lecturer.id} className="rounded-md border border-borderGrey bg-card px-3.5 py-3">
          <span className="block text-base font-semibold leading-snug text-primaryTextColor">{lecturer.name}</span>
          {lecturer.personalNumber != null && lecturer.personalNumber !== '' && (
            <span className="mt-1.5 block text-sm font-medium tabular-nums text-captionColor">
              {lecturer.personalNumber}
            </span>
          )}
        </li>
      ))}
    </ul>
  )
}

function formatScheduleDisplayDate(date: string): string {
  if (date === '') return ''
  const parts = date.split('-')
  return parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : date
}

function formatScheduleDuplicateMessage(
  duplicate: Api.AdministratorCourseGroupScheduleDuplicateItem,
  t: ReturnType<typeof useTranslatable>
): string {
  const date = formatScheduleDisplayDate(duplicate.date)
  return t([
    `ლექცია/სემინარი ემთხვევა კურსს - ${duplicate.courseName} , მოცემულ თარიღში ${date} , ${duplicate.groupName}`,
    `Lecture/seminar overlaps with course - ${duplicate.courseName}, on date ${date}, ${duplicate.groupName}`,
  ])
}

function ScheduleFormScrollToError(): null {
  const [formData] = useFormData()

  React.useEffect(() => {
    const hasErrors = formData.errors.general.length > 0 || Object.keys(formData.errors.fields).length > 0

    if (!formData.submitted || !hasErrors) return

    const timeout = window.setTimeout(() => {
      const scrollContainer = document.querySelector<HTMLElement>('.schedule-modal-scroll')
      scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' })
    }, 50)

    return () => window.clearTimeout(timeout)
  }, [formData.submitted, formData.errors])

  return null
}

function ScheduleDuplicatesAlert({
  duplicates,
}: {
  readonly duplicates: readonly Api.AdministratorCourseGroupScheduleDuplicateItem[] | undefined
}): JSX.Element | null {
  const t = useTranslatable()
  const messages = React.useMemo(
    () => (duplicates ?? []).map((duplicate) => formatScheduleDuplicateMessage(duplicate, t)),
    [duplicates, t]
  )

  if (messages.length === 0) return null

  if (messages.length === 1) {
    return (
      <div
        className="form-general-error my-3 flex items-center rounded-[4px] bg-seconderyRed/20 px-4 py-3 text-primaryRed"
        role="alert"
      >
        <div>
          <div className="mr-[11px] rounded-full bg-primaryRed p-1">
            <CloseIcon width="10" height="10" />
          </div>
        </div>
        <div>
          <div className="mb-1 font-bold">{t('course:lecture_conflict')}</div>
          {messages[0]}
        </div>
      </div>
    )
  }

  return (
    <div
      className="form-general-error my-3 flex items-center rounded-[4px] bg-seconderyRed/20 px-4 py-3 font-bold text-primaryRed"
      role="alert"
    >
      <div>
        <div className="mr-[11px] rounded-full bg-primaryRed p-1">
          <CloseIcon width="12" />
        </div>
      </div>
      <div>
        <div className="mb-1 font-bold">
          {t('course:lecture_conflict')}
          <ul>
            {messages.map((text, index) => (
              <li key={index}>{text}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}

function parseScheduleNumericId(id: Api.IDString): number {
  const parsed = typeof id === 'number' ? id : parseInt(String(id), 10)
  if (Number.isNaN(parsed)) {
    throw new Error(`Invalid schedule id: ${String(id)}`)
  }
  return parsed
}

function parseLastSelectedHourId(hourIds: readonly string[]): number | null {
  if (hourIds.length === 0) return null
  const parsed = parseInt(hourIds[hourIds.length - 1]!, 10)
  return Number.isNaN(parsed) ? null : parsed
}

function mapScheduleToFormValues(schedule: Api.AdministratorCourseGroupSchedule): ScheduleFormValues {
  return {
    hourIds: schedule.hour != null ? [String(schedule.hour.id)] : [],
    learningWeekDates: [],
    date: schedule.date,
    lectureTypeId: schedule.lectureType?.id != null ? String(schedule.lectureType.id) : '',
    lectureFormatId: schedule.lectureFormat?.id != null ? String(schedule.lectureFormat.id) : '',
    locationType:
      schedule.locationType === 2 ? '2' : schedule.locationType === 1 ? '1' : schedule.locationType == null ? '' : '',
    buildingId: schedule.building != null ? String(schedule.building.id) : '',
    roomId: schedule.room != null ? String(schedule.room.id) : '',
    locationId: schedule.location?.id != null && schedule.location.id !== '' ? String(schedule.location.id) : '',
    lecturers: (schedule.lecturers ?? []).map((l) => ({
      userId: String(l.userId),
      profileId: String(l.profileId),
      isPaid: l.isPaid ?? false,
    })),
    info: schedule.info ?? '',
    isHalfHour: schedule.isHalfHour ?? false,
    isSplitLecture: schedule.isSplitLecture ?? false,
    massUpdate: false,
    force: false,
  }
}

function buildScheduleBodyFields(
  values: ScheduleFormValues,
  hourId: number
): {
  readonly hourId: number
  readonly lectureTypeId: number
  readonly lectureFormatId: number
  readonly locationType?: number
  readonly buildingId?: number
  readonly roomId?: number
  readonly locationId?: number
  readonly isHalfHour: boolean
  readonly isSplitLecture: boolean
  readonly info?: string
  readonly lecturers: readonly Api.AdministratorCourseGroupScheduleLecturer[]
} {
  const formatKind = getLectureFormatKind(values.lectureFormatId)

  const shared = {
    hourId,
    lectureTypeId: parseInt(values.lectureTypeId, 10),
    lectureFormatId: parseInt(values.lectureFormatId, 10),
    isHalfHour: values.isHalfHour,
    isSplitLecture: values.isSplitLecture,
    ...(values.info.trim() !== '' && { info: values.info.trim() }),
    lecturers: values.lecturers.map((l) => ({
      userId: l.userId,
      profileId: l.profileId,
      isPaid: l.isPaid ?? false,
    })),
  }

  if (formatKind === 'online') {
    return shared
  }

  const locationType = parseInt(values.locationType || (formatKind === 'offline' ? '1' : ''), 10)

  if (Number.isNaN(locationType)) {
    throw new Error('LOCATION_TYPE_REQUIRED')
  }
  if (locationType === 2 && values.locationId === '') {
    throw new Error('LOCATION_ID_REQUIRED')
  }

  return {
    ...shared,
    locationType,
    ...(locationType === 1 && values.buildingId !== '' && { buildingId: parseInt(values.buildingId, 10) }),
    ...(locationType === 1 && values.roomId !== '' && { roomId: parseInt(values.roomId, 10) }),
    ...(locationType === 2 && values.locationId !== '' && { locationId: parseInt(values.locationId, 10) }),
  }
}

type ScheduleConflictCheckBody = Omit<ReturnType<typeof buildScheduleBodyFields>, 'hourId'> & {
  readonly hourIds: readonly number[]
  readonly dates: readonly string[]
}

async function assertNoScheduleConflicts(
  headers: AuthenticatedHeaders,
  couId: number,
  groupId: number,
  semId: number,
  body: ScheduleConflictCheckBody,
  scheduleId?: number
): Promise<void> {
  const result = await Api.postAdministratorCourseGroupScheduleCheckConflicts({
    headers,
    args: { couId, groupId },
    body: { semId, ...body, ...(scheduleId != null && { scheduleId }) },
  })

  if (!result.hasConflicts) return

  const allConflicts: Api.AdministratorCourseGroupScheduleConflictNotice[] = []
  for (const hourEntries of Object.values(result.results)) {
    for (const entry of Object.values(hourEntries)) {
      if (entry.hasConflicts) {
        allConflicts.push(...entry.conflicts)
      }
    }
  }

  if (allConflicts.length === 0) return

  const messages = allConflicts.flatMap((conflict) => conflict.messages)
  if (messages.length === 0) return

  const conflictTypes = allConflicts.map((conflict) => conflict.type)
  throw { type: 'ScheduleConflictsPending' as const, errors: { general: messages }, conflictTypes }
}

function isScheduleConflictsPendingError(error: unknown): error is {
  readonly type: 'ScheduleConflictsPending'
  readonly errors: { readonly general: readonly string[] }
  readonly conflictTypes?: readonly string[]
} {
  return error != null && typeof error === 'object' && (error as { type: string }).type === 'ScheduleConflictsPending'
}

function getConflictTypesFromError(error: unknown): readonly string[] {
  if (isScheduleConflictsPendingError(error) && error.conflictTypes) {
    return error.conflictTypes
  }
  if (error != null && typeof error === 'object' && 'type' in error) {
    if ((error as { type: string }).type === 'ScheduleConflictsDetected') {
      return (error as Api.AdministratorCourseGroupScheduleConflictsDetected).conflicts.map((c) => c.type)
    }
    if (
      (error as { type: string }).type === 'ErrorMessage' &&
      'conflicts' in error &&
      Array.isArray((error as { conflicts: unknown }).conflicts)
    ) {
      return (error as { conflicts: Api.AdministratorCourseGroupScheduleConflictNotice[] }).conflicts.map((c) => c.type)
    }
  }
  return []
}

function handleScheduleSubmitConflictError(
  error: unknown,
  isMainAdministrator: boolean,
  setConflictTypes: (types: readonly string[]) => void
): never {
  if (isScheduleConflictsPendingError(error)) {
    setConflictTypes(isMainAdministrator ? getConflictTypesFromError(error) : [])
    throw { type: 'ErrorsObject' as const, errors: error.errors }
  }

  const conflictMessages = getScheduleConflictMessages(error)
  if (conflictMessages != null && conflictMessages.length > 0) {
    setConflictTypes(isMainAdministrator ? getConflictTypesFromError(error) : [])
    throw { type: 'ErrorsObject' as const, errors: { general: conflictMessages } }
  }

  setConflictTypes([])
  throw error
}

type LectureFormatKind = 'offline' | 'online' | 'hybrid'

function getLectureFormatKind(formatId: string): LectureFormatKind {
  if (formatId === '') return 'offline'
  const id = parseInt(formatId, 10)
  if (id === 2) return 'online'
  if (id === 3) return 'hybrid'
  return 'offline'
}

function mapGroupLecturersToAvailable(
  lecturers: readonly Api.AdministratorCourseGroupLecturer[]
): readonly Api.AdministratorCourseGroupAvailableLecturer[] {
  return lecturers.map((l) => ({
    userId: l.userId ?? l.id,
    profileId: l.profileId ?? l.id,
    name: l.fullName,
    personalNumber: l.personalNumber ?? '',
    degree: l.degree ?? null,
  }))
}

function formatAvailableLecturerName(lecturer: Api.AdministratorCourseGroupAvailableLecturer): string {
  return lecturer.personalNumber !== '' ? `${lecturer.name} (${lecturer.personalNumber})` : lecturer.name
}

function formatAvailableLecturerPickerLabel(lecturer: Api.AdministratorCourseGroupAvailableLecturer): string {
  const degreeLabel = formatAvailableLecturerDegree(lecturer.degree)
  const namePart = formatAvailableLecturerName(lecturer)
  return degreeLabel !== '' ? `${namePart} — ${degreeLabel}` : namePart
}

const LECTURER_DEGREE_BY_NUMBER: Record<number, Api.LecturerDegree> = {
  1: 'LECTURER_PROFESSOR',
  2: 'LECTURER_ASSOC_PROFESSOR',
  3: 'LECTURER_ASSISTANT_PROFESSOR',
  4: 'LECTURER_INVITED_LECTURER',
  5: 'LECTURER_PROFESSOR_EMERITUS',
  6: 'LECTURER_RESP_PROFESSOR',
  7: 'LECTURER_ASSISTANT',
  8: 'LECTURER_COURSE_CURATOR',
  9: 'LECTURER_ACADEMIC_PERSONAL',
  10: 'LECTURER_TEACHER',
}

function formatAvailableLecturerDegree(degree: string | number | null | undefined): string {
  if (degree == null || degree === '') return ''
  if (typeof degree === 'number') {
    const enumVal = LECTURER_DEGREE_BY_NUMBER[degree]
    return enumVal != null ? getLecturerDegreeName(enumVal) : String(degree)
  }
  if ((Api.LecturerDegreeValues as readonly string[]).includes(degree)) {
    return getLecturerDegreeName(degree as Api.LecturerDegree)
  }
  const numericDegree = parseInt(degree, 10)
  if (!Number.isNaN(numericDegree) && LECTURER_DEGREE_BY_NUMBER[numericDegree] != null) {
    return getLecturerDegreeName(LECTURER_DEGREE_BY_NUMBER[numericDegree]!)
  }
  return degree
}

function PageContent(): JSX.Element | null {
  const t = useTranslatable()
  const locale = useLocale()
  const headers = useAuthenticatedHeaders()
  const { isMainAdministrator, canManage } = useAdminAccess()
  const canManageCourses = canManage('courses')
  const { courseId } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()

  const groupIdParam = searchParams.get('groupId')
  const semIdParam = searchParams.get('semId')
  const scheduleIdParam = searchParams.get('scheduleId')
  const parsedGroupId = groupIdParam != null && groupIdParam !== '' ? parseInt(groupIdParam, 10) : null
  const parsedSemId = semIdParam != null && semIdParam !== '' ? parseInt(semIdParam, 10) : null

  const filterInitialValues = React.useMemo(() => filterValuesFromSearchParams(searchParams), [searchParams])
  const isFilterActive = isScheduleFilterActive(searchParams)

  const [showFilter, setShowFilter] = React.useState(() => isScheduleFilterActive(searchParams))
  const [showAddSchedule, setShowAddSchedule] = React.useState(false)
  const [editingSchedule, setEditingSchedule] = React.useState<Api.AdministratorCourseGroupSchedule | null>(null)
  const [editModalKey, setEditModalKey] = React.useState(0)
  const [addScheduleConflictTypes, setAddScheduleConflictTypes] = React.useState<readonly string[]>([])
  const [editScheduleConflictTypes, setEditScheduleConflictTypes] = React.useState<readonly string[]>([])
  // setSearchParams changes identity on every URL change; read it through a ref so memoized cards keep a stable onEdit
  const setSearchParamsRef = useLatest(setSearchParams)
  const openEditSchedule = React.useCallback(
    (schedule: Api.AdministratorCourseGroupSchedule) => {
      setEditingSchedule(schedule)
      setEditModalKey((k) => k + 1)
      setSearchParamsRef.current((params) => {
        params.set('scheduleId', String(schedule.id))
        return params
      })
    },
    [setSearchParamsRef]
  )

  const clearAddScheduleConflicts = React.useCallback(() => {
    setAddScheduleConflictTypes([])
  }, [])

  const clearEditScheduleConflicts = React.useCallback(() => {
    setEditScheduleConflictTypes([])
  }, [])

  const closeEditSchedule = React.useCallback(() => {
    setEditScheduleConflictTypes([])
    setEditingSchedule(null)
    setSearchParams((params) => {
      params.delete('scheduleId')
      return params
    })
  }, [setSearchParams])

  React.useEffect(() => {
    if (isFilterActive) {
      setShowFilter(true)
    }
  }, [isFilterActive])

  const { data: groups, isLoading: isGroupsLoading } = useApi({
    endpoint: Api.getAdministratorCourseGroups,
    params: React.useMemo(
      () => ({
        headers,
        args: { id: courseId! },
        query: parsedSemId != null ? { semId: parsedSemId } : {},
      }),
      [courseId, headers, parsedSemId]
    ),
    shouldFetch: courseId != null,
    suspense: false,
  })

  const scheduleListQuery = React.useMemo(
    () => (parsedSemId != null ? buildScheduleListQuery(parsedSemId, searchParams) : null),
    [parsedSemId, searchParams]
  )

  const {
    data: schedules,
    isLoading: isSchedulesLoading,
    isValidating: isSchedulesValidating,
    mutate: mutateSchedules,
  } = useApi({
    endpoint: Api.getAdministratorCourseGroupSchedules,
    params: React.useMemo(
      () => ({
        headers,
        args: { couId: parseInt(courseId!, 10), groupId: parsedGroupId! },
        query: scheduleListQuery!,
      }),
      [courseId, headers, parsedGroupId, scheduleListQuery]
    ),
    shouldFetch: courseId != null && parsedGroupId != null && scheduleListQuery != null,
    suspense: false,
  })

  const isScheduleContentLoading = isGroupsLoading || isSchedulesLoading || isSchedulesValidating

  React.useEffect(() => {
    if (scheduleIdParam == null || scheduleIdParam === '' || schedules == null) return
    if (editingSchedule != null && String(editingSchedule.id) === scheduleIdParam) return

    const schedule = schedules.find((item) => String(item.id) === scheduleIdParam)
    if (schedule != null) {
      setEditingSchedule(schedule)
    }
  }, [scheduleIdParam, schedules, editingSchedule])

  const { data: hoursData } = useApi({
    endpoint: Api.getAdministratorListsHours,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: lectureTypesData, isLoading: isLectureTypesLoading } = useApi({
    endpoint: Api.getAdministratorListsLectureTypes,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const { data: formatsData, isLoading: isFormatsLoading } = useApi({
    endpoint: Api.getAdministratorListsLectureFormats,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const group = React.useMemo(() => groups?.find((g) => g.id === groupIdParam), [groups, groupIdParam])

  const lecturerPersonalNumberByUserId = React.useMemo(() => {
    const map = new Map<string, string>()
    for (const lecturer of group?.lecturers ?? []) {
      const userId = lecturer.userId ?? lecturer.id
      if (lecturer.personalNumber != null && lecturer.personalNumber !== '') {
        map.set(String(userId), lecturer.personalNumber)
      }
    }
    return map
  }, [group?.lecturers])

  const { data: allLearningWeeks } = useApi({
    endpoint: Api.getAdministratorListsLearningWeeks,
    params: React.useMemo(
      () => ({
        headers,
        query: { semId: parsedSemId!, couId: parseInt(courseId!, 10), groupId: parsedGroupId! },
      }),
      [headers, parsedSemId, courseId, parsedGroupId]
    ),
    shouldFetch: parsedSemId != null && courseId != null && parsedGroupId != null,
    suspense: false,
  })

  const weekLabelByDate = React.useMemo(() => {
    const map = new Map<string, string>()
    for (const week of allLearningWeeks ?? []) {
      const label = week.weekLabel !== '' ? week.weekLabel : week.label !== '' ? week.label : ''
      if (label !== '') {
        map.set(week.date, label)
      }
    }
    return map
  }, [allLearningWeeks])

  const filterHourOptions = React.useMemo(
    () => [
      { value: '', label: t('common:select') },
      ...(hoursData?.data ?? []).map((h) => ({
        value: String(h.id),
        label: formatHour(h, locale),
      })),
    ],
    [hoursData, locale, t]
  )

  const filterLectureTypeOptions = React.useMemo(
    () => [
      { value: '', label: t('common:select') },
      ...(lectureTypesData?.data ?? []).map((lt) => ({
        value: String(lt.id),
        label: lt.name,
      })),
    ],
    [lectureTypesData, t]
  )

  const filterLectureFormatOptions = React.useMemo(
    () => [
      { value: '', label: t('common:select') },
      ...(formatsData?.data ?? []).map((f) => ({
        value: String(f.id),
        label: f.name,
      })),
    ],
    [formatsData, t]
  )

  const addScheduleLectureTypeOptions = React.useMemo(
    () =>
      (lectureTypesData?.data ?? []).map((lt) => ({
        value: String(lt.id),
        label: lt.name,
      })),
    [lectureTypesData]
  )

  const addScheduleInitialValues = React.useMemo(
    () => createAddScheduleInitialValues(lectureTypesData?.data, formatsData?.data),
    [lectureTypesData, formatsData]
  )

  const handleFilterSubmit = React.useCallback(
    (values: FilterValues) => {
      setSearchParams((params) => {
        FILTER_SEARCH_PARAM_KEYS.forEach((key) => {
          const value = values[key]
          if (value !== '') {
            params.set(key, value)
          } else {
            params.delete(key)
          }
        })
        return params
      })
    },
    [setSearchParams]
  )

  const handleAddScheduleSubmit = React.useCallback(
    async (values: ScheduleFormValues, formikHelpers: FormikHelpers<ScheduleFormValues>) => {
      if (values.hourIds.length === 0) {
        throwScheduleFormError(t(['გთხოვთ აირჩიოთ საათი', 'Please choose hour']))
      }
      if (values.learningWeekDates.length === 0) {
        throwScheduleFormError(t(['გთხოვთ აირჩიოთ სასწავლო კვირები', 'Please choose learning weeks']))
      }

      const force = values.force
      const couId = parseInt(courseId!, 10)
      const hourIds = values.hourIds.map((id) => parseInt(id, 10))

      let bodyBase: ReturnType<typeof buildScheduleBodyFields>
      try {
        bodyBase = buildScheduleBodyFields(values, hourIds[0]!)
      } catch (error) {
        rethrowBuildScheduleError(error, t)
      }

      try {
        if (!force) {
          await assertNoScheduleConflicts(headers, couId, parsedGroupId!, parsedSemId!, {
            ...bodyBase,
            hourIds,
            dates: [...values.learningWeekDates],
          })
        }

        for (const hourId of hourIds) {
          const hourBodyBase = buildScheduleBodyFields(values, hourId)
          for (const date of values.learningWeekDates) {
            await Api.postAdministratorCourseGroupSchedule({
              headers,
              args: { couId, groupId: parsedGroupId! },
              body: { semId: parsedSemId!, ...hourBodyBase, date, ...(force && { force: true }) },
            })
          }
        }
        setAddScheduleConflictTypes([])
        await mutateSchedules()
        setShowAddSchedule(false)
        toast.success(t(['ცხრილი წარმატებით დაემატა', 'Schedule added successfully']))
      } catch (error) {
        try {
          handleScheduleSubmitConflictError(error, isMainAdministrator, setAddScheduleConflictTypes)
        } catch (handledError) {
          rethrowBuildScheduleError(handledError, t)
        }
      } finally {
        await formikHelpers.setFieldValue('force', false)
      }
    },
    [courseId, headers, isMainAdministrator, mutateSchedules, parsedGroupId, parsedSemId, t]
  )

  const handleEditScheduleSubmit = React.useCallback(
    async (values: ScheduleFormValues, formikHelpers: FormikHelpers<ScheduleFormValues>) => {
      if (editingSchedule == null) return
      if (values.hourIds.length === 0) {
        throwScheduleFormError(t(['გთხოვთ აირჩიოთ საათი', 'Please choose hour']))
      }

      let bodyFields: ReturnType<typeof buildScheduleBodyFields>
      try {
        bodyFields = buildScheduleBodyFields(values, parseInt(values.hourIds[0]!, 10))
      } catch (error) {
        rethrowBuildScheduleError(error, t)
      }

      const force = values.force

      try {
        const couId = parseInt(courseId!, 10)
        const scheduleBody = { ...bodyFields, date: values.date }
        if (!force && !values.massUpdate) {
          await assertNoScheduleConflicts(
            headers,
            couId,
            parsedGroupId!,
            parsedSemId!,
            {
              ...bodyFields,
              hourIds: [parseInt(values.hourIds[0]!, 10)],
              dates: [values.date],
            },
            parseScheduleNumericId(editingSchedule.id)
          )
        }
        await Api.putAdministratorCourseGroupSchedule({
          headers,
          args: {
            couId,
            groupId: parsedGroupId!,
            id: parseScheduleNumericId(editingSchedule.id),
          },
          query: { semId: parsedSemId! },
          body: {
            ...scheduleBody,
            ...(values.massUpdate && { massUpdate: true }),
            ...(force && { force: true }),
          },
        })
        setEditScheduleConflictTypes([])
        await mutateSchedules()
        closeEditSchedule()
        toast.success(t(['ცხრილი წარმატებით განახლდა', 'Schedule updated successfully']))
      } catch (error) {
        try {
          handleScheduleSubmitConflictError(error, isMainAdministrator, setEditScheduleConflictTypes)
        } catch (handledError) {
          rethrowBuildScheduleError(handledError, t)
        }
      } finally {
        await formikHelpers.setFieldValue('force', false)
      }
    },
    [
      closeEditSchedule,
      courseId,
      editingSchedule,
      headers,
      isMainAdministrator,
      mutateSchedules,
      parsedGroupId,
      parsedSemId,
      t,
    ]
  )

  const editScheduleInitialValues = React.useMemo(
    () => (editingSchedule != null ? mapScheduleToFormValues(editingSchedule) : DEFAULT_SCHEDULE_FORM),
    [editingSchedule]
  )

  const handleDeleteSchedule = React.useCallback(
    async (schedule: Api.AdministratorCourseGroupSchedule) => {
      if (!canModifySchedule(schedule.date, isMainAdministrator, canManageCourses)) return

      const alert = await Swal.fire({
        title: t('common:are_you_sure_to_delete'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: t('common:confirm'),
        cancelButtonText: t('common:cancel'),
        confirmButtonColor: '#dc3545',
        cancelButtonColor: '#6C757D',
        html: `<label style="display:flex;align-items:center;gap:8px;margin-top:12px;cursor:pointer;font-size:14px;">
          <input type="checkbox" id="swal-mass-delete" style="width:16px;height:16px;" />
          ${t(['ყველა მსგავსი ცხრილის ჩანაწერის წაშლა', 'Delete All Schedules Matching Selected Schedule'])}
        </label>`,
        preConfirm: () => {
          const checkbox = document.getElementById('swal-mass-delete') as HTMLInputElement | null
          return { massDelete: checkbox?.checked ?? false }
        },
      })
      if (!alert.isConfirmed) return

      const massDelete = alert.value?.massDelete ?? false

      try {
        await Api.deleteAdministratorCourseGroupSchedule({
          headers,
          args: {
            couId: parseInt(courseId!, 10),
            groupId: parsedGroupId!,
            id: parseInt(schedule.id, 10),
          },
          query: { semId: parsedSemId!, ...(massDelete && { massDelete: true }) },
        })
        void mutateSchedules()
        toast.success(t(['წარმატებით წაიშალა', 'Successfully deleted']))
      } catch (error) {
        toast.error(errorMessage(error as base.CommonError))
      }
    },
    [canManageCourses, courseId, headers, isMainAdministrator, mutateSchedules, parsedGroupId, parsedSemId, t]
  )

  if (courseId == null) return null

  if (parsedGroupId == null || parsedSemId == null) {
    return (
      <NoContent
        header={t('common:schedule')}
        subHeader={t([
          'ჯგუფი არ არის არჩეული. გთხოვთ, ჯგუფების გვერდიდან გადახვიდეთ.',
          'No group selected. Please navigate from the groups page.',
        ])}
        image={Warning}
        marginTop="5"
      />
    )
  }

  return (
    <div className="mt-4 w-full space-y-6">
      {/* Group info card */}
      {!isScheduleContentLoading && (
        <fieldset className="min-w-0 rounded-card bg-card px-6 py-8">
          <FormSection title={t(['ჯგუფის ინფორმაცია', 'Group Information'])} className="mb-0">
            <div className="space-y-4">
              <div>
                <span className="mb-1.5 block text-sm font-medium text-primaryTextColor">
                  {t('portfolio:description')}
                </span>
                <p className="text-base font-medium text-primaryTextColor">{group?.name ?? '—'}</p>
              </div>
              <div>
                <span className="mb-3 block text-sm font-medium text-primaryTextColor">{t('lecturer:lecturers')}</span>
                {group?.lecturers != null && group.lecturers.length > 0 ? (
                  <LecturerDetailsList
                    lecturers={group.lecturers.map((lecturer) => ({
                      id: lecturer.id,
                      name: lecturer.fullName,
                      personalNumber: lecturer.personalNumber,
                    }))}
                  />
                ) : (
                  <p className="text-base text-captionColor">—</p>
                )}
              </div>
            </div>
          </FormSection>
        </fieldset>
      )}

      {/* Toolbar */}
      <div className="flex flex-col items-end gap-2">
        {canManageCourses && (
          <Button variant="blue" onClick={() => setShowAddSchedule(true)}>
            <FontAwesomeIcon icon={ProRegularSvgIcons.faPlus} className="mr-2 size-3.5" />
            {t(['ცხრილის დამატება', 'Add Schedule'])}
          </Button>
        )}
        <Button
          variant={isFilterActive || showFilter ? 'outline' : 'yellow'}
          type="button"
          onClick={() => setShowFilter((v) => !v)}
        >
          <FontAwesomeIcon icon={ProRegularSvgIcons.faFilter} className="mr-2 size-3.5" />
          {t(['ფილტრი', 'Filter'])}
        </Button>
      </div>

      {showFilter && (
        <fieldset className="min-w-0 rounded-card bg-card px-6 py-8">
          <Form<FilterValues>
            initialValues={filterInitialValues}
            onSubmit={handleFilterSubmit}
            classNames={{ form: 'w-full' }}
            enableReinitialize
          >
            <FilterForm
              headers={headers}
              couId={parseInt(courseId!, 10)}
              groupId={parsedGroupId!}
              semId={parsedSemId!}
              hourOptions={filterHourOptions}
              lectureTypeOptions={filterLectureTypeOptions}
              lectureFormatOptions={filterLectureFormatOptions}
              isLectureTypesLoading={isLectureTypesLoading}
              isFormatsLoading={isFormatsLoading}
            />
          </Form>
        </fieldset>
      )}

      {/* Schedule list */}
      {isScheduleContentLoading ? (
        <Loader className="m-auto my-8 flex" />
      ) : schedules == null || schedules.length === 0 ? (
        isFilterActive ? (
          <NoContent
            header={t('error:records_not_found')}
            image={Warning}
            subHeader={t(['შედეგი ვერ მოიძებნა', 'No results found'])}
            marginTop="0"
          />
        ) : (
          <NoContent header={t('schedule:schedule_is_empty')} image={Warning} marginTop="0" />
        )
      ) : (
        <ul className="w-full space-y-3">
          {schedules.map((schedule) => (
            <ScheduleCard
              key={schedule.id}
              schedule={schedule}
              locale={locale}
              lecturerPersonalNumberByUserId={lecturerPersonalNumberByUserId}
              weekLabelByDate={weekLabelByDate}
              canModify={canModifySchedule(schedule.date, isMainAdministrator, canManageCourses)}
              onEdit={openEditSchedule}
              onDelete={handleDeleteSchedule}
            />
          ))}
        </ul>
      )}

      {/* Add Schedule Modal */}
      <Modal
        title={t(['ცხრილის დამატება', 'Create Schedule'])}
        isModalOpen={showAddSchedule}
        onClose={() => {
          clearAddScheduleConflicts()
          setShowAddSchedule(false)
        }}
      >
        <div className="schedule-modal-scroll max-h-[55vh] min-h-[300px] overflow-y-auto">
          {showAddSchedule && (
            <Form<ScheduleFormValues>
              key={`${getFirstListItemId(lectureTypesData?.data)}-${getFirstListItemId(formatsData?.data)}`}
              initialValues={addScheduleInitialValues}
              onSubmit={handleAddScheduleSubmit}
              classNames={{ form: 'w-full' }}
              isConfirmable
            >
              <ScheduleFormContent
                mode="add"
                headers={headers}
                hours={hoursData?.data ?? []}
                locale={locale}
                lectureTypeOptions={addScheduleLectureTypeOptions}
                couId={parseInt(courseId!, 10)}
                groupId={parsedGroupId!}
                semId={parsedSemId!}
                groupLecturers={group?.lecturers ?? []}
                scheduleConflictsPending={addScheduleConflictTypes.length > 0}
                canForceSubmit={addScheduleConflictTypes.some((t) => t === 'room')}
                onClearScheduleConflicts={clearAddScheduleConflicts}
                onCancel={() => {
                  clearAddScheduleConflicts()
                  setShowAddSchedule(false)
                }}
                isMainAdministrator={isMainAdministrator}
              />
            </Form>
          )}
        </div>
      </Modal>

      <Modal
        title={
          editingSchedule != null
            ? `${t(['ცხრილის რედაქტირა', 'Edit Schedule'])} - ${formatScheduleDisplayDate(editingSchedule.date)}, ${t(['ჯგუფი', 'Group'])}: ${group?.name ?? ''}`
            : t(['ცხრილის რედაქტირა', 'Edit Schedule'])
        }
        isModalOpen={editingSchedule != null}
        onClose={closeEditSchedule}
      >
        <div className="schedule-modal-scroll max-h-[55vh] min-h-[300px] overflow-y-auto">
          {editingSchedule != null && (
            <Form<ScheduleFormValues>
              key={`${editingSchedule.id}-${editModalKey}`}
              initialValues={editScheduleInitialValues}
              onSubmit={handleEditScheduleSubmit}
              classNames={{ form: 'w-full' }}
              isConfirmable
              enableReinitialize
            >
              <ScheduleFormContent
                mode="edit"
                headers={headers}
                hours={hoursData?.data ?? []}
                locale={locale}
                lectureTypeOptions={addScheduleLectureTypeOptions}
                couId={parseInt(courseId!, 10)}
                groupId={parsedGroupId!}
                semId={parsedSemId!}
                groupLecturers={group?.lecturers ?? []}
                duplicates={editingSchedule.duplicates}
                scheduleConflictsPending={editScheduleConflictTypes.length > 0}
                canForceSubmit={editScheduleConflictTypes.some((t) => t === 'room')}
                onClearScheduleConflicts={clearEditScheduleConflicts}
                onCancel={closeEditSchedule}
                isMainAdministrator={isMainAdministrator}
              />
            </Form>
          )}
        </div>
      </Modal>
    </div>
  )
}

// ─── Filter form ──────────────────────────────────────────────────────────────

interface FilterFormProps {
  readonly headers: AuthenticatedHeaders
  readonly couId: number
  readonly groupId: number
  readonly semId: number
  readonly hourOptions: readonly { value: string; label: string }[]
  readonly lectureTypeOptions: readonly { value: string; label: string }[]
  readonly lectureFormatOptions: readonly { value: string; label: string }[]
  readonly isLectureTypesLoading: boolean
  readonly isFormatsLoading: boolean
}

function FilterForm({
  headers,
  couId,
  groupId,
  semId,
  hourOptions,
  lectureTypeOptions,
  lectureFormatOptions,
  isLectureTypesLoading,
  isFormatsLoading,
}: FilterFormProps): JSX.Element {
  const formik = useFormikContext<FilterValues>()
  const t = useTranslatable()
  const [searchParams, setSearchParams] = useSearchParams()

  const appliedFilter = React.useMemo(() => filterValuesFromSearchParams(searchParams), [searchParams])

  const { data: learningWeeksData, isLoading: isLearningWeeksLoading } = useApi({
    endpoint: Api.getAdministratorListsLearningWeeks,
    params: React.useMemo(
      () => ({
        headers,
        query: { semId, couId, groupId },
      }),
      [headers, semId, couId, groupId]
    ),
    suspense: false,
  })

  const weekOptions = React.useMemo(
    () => [
      { value: '', label: t('common:select') },
      ...(learningWeeksData ?? []).map((d) => ({
        value: String(d.weekNumber),
        label: d.weekLabel !== '' ? d.weekLabel : d.label !== '' ? d.label : d.date,
      })),
    ],
    [learningWeeksData, t]
  )

  const filterFormatKind = getLectureFormatKind(formik.values.lectureFormat)
  const showFilterLocationType = filterFormatKind !== 'online' && formik.values.lectureFormat !== ''
  const showFilterBuildingRoom = showFilterLocationType && formik.values.locationType === '1'
  const showFilterOtherLocation = showFilterLocationType && formik.values.locationType === '2'

  const prevFilterFormatRef = React.useRef(formik.values.lectureFormat)
  React.useEffect(() => {
    const prev = prevFilterFormatRef.current
    prevFilterFormatRef.current = formik.values.lectureFormat
    if (prev === formik.values.lectureFormat) return
    void formik.setFieldValue('locationType', '')
    void formik.setFieldValue('buildingId', '')
    void formik.setFieldValue('roomId', '')
    void formik.setFieldValue('locationId', '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.lectureFormat])

  const prevFilterLocationTypeRef = React.useRef(formik.values.locationType)
  React.useEffect(() => {
    const prev = prevFilterLocationTypeRef.current
    prevFilterLocationTypeRef.current = formik.values.locationType
    if (prev === formik.values.locationType) return
    void formik.setFieldValue('buildingId', '')
    void formik.setFieldValue('roomId', '')
    void formik.setFieldValue('locationId', '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.locationType])

  const prevFilterBuildingIdRef = React.useRef(formik.values.buildingId)
  React.useEffect(() => {
    const prev = prevFilterBuildingIdRef.current
    prevFilterBuildingIdRef.current = formik.values.buildingId
    if (prev !== formik.values.buildingId) {
      void formik.setFieldValue('roomId', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.buildingId])

  const { data: filterBuildingsData, isLoading: isFilterBuildingsLoading } = useApi({
    endpoint: Api.getAdministratorListsBuildings,
    params: React.useMemo(() => ({ headers }), [headers]),
    shouldFetch: showFilterBuildingRoom,
    suspense: false,
  })

  const { data: filterRoomsData, isLoading: isFilterRoomsLoading } = useApi({
    endpoint: Api.getAdministratorListsRooms,
    params: React.useMemo(
      () => ({
        headers,
        query: formik.values.buildingId !== '' ? { buildingId: parseInt(formik.values.buildingId, 10) } : {},
      }),
      [headers, formik.values.buildingId]
    ),
    shouldFetch: showFilterBuildingRoom && formik.values.buildingId !== '',
    suspense: false,
  })

  const { data: filterLocationsData, isLoading: isFilterLocationsLoading } = useApi({
    endpoint: Api.getAdministratorListsScheduleLocations,
    params: React.useMemo(() => ({ headers }), [headers]),
    shouldFetch: showFilterOtherLocation,
    suspense: false,
  })

  const filterBuildingOptions = React.useMemo(
    () => [
      { value: '', label: t(['გთხოვთ აირჩიოთ კორპუსი', 'Please Choose Building']) },
      ...(filterBuildingsData?.data ?? []).map((b) => ({ value: String(b.id), label: b.name })),
    ],
    [filterBuildingsData, t]
  )

  const filterRoomOptions = React.useMemo(
    () =>
      formik.values.buildingId === ''
        ? [{ value: '', label: t('common:select') }]
        : [
            { value: '', label: t('common:select') },
            ...(filterRoomsData?.data ?? []).map((r) => ({ value: String(r.id), label: r.name })),
          ],
    [formik.values.buildingId, filterRoomsData, t]
  )

  const filterLocationOptions = React.useMemo(() => {
    const placeholder = { value: '', label: t(['გთხოვთ აირჩიოთ ლოკაცია', 'Please Choose Location']) }
    const fromApi = (filterLocationsData?.data ?? [])
      .filter((l) => l.id > 0)
      .map((l) => {
        const label = l.title.trim()
        return {
          value: String(l.id),
          label: label !== '' ? label : `${t(['ლოკაცია', 'Location'])} #${l.id}`,
        }
      })
    return [placeholder, ...fromApi]
  }, [filterLocationsData, t])

  const filterLocationTypeOptions = React.useMemo(() => {
    const options = [
      { value: '1', label: t(['უნივერსიტეტი', 'University']) },
      { value: '2', label: t(['სხვა', 'Other']) },
    ]
    if (filterFormatKind === 'hybrid') {
      return [{ value: '', label: t(['გთხოვთ აირჩიოთ', 'Please choose']) }, ...options]
    }
    return options
  }, [filterFormatKind, t])

  const isDirty =
    formik.values.weekId !== appliedFilter.weekId ||
    formik.values.hourId !== appliedFilter.hourId ||
    formik.values.lectureType !== appliedFilter.lectureType ||
    formik.values.lectureFormat !== appliedFilter.lectureFormat ||
    formik.values.searchWord !== appliedFilter.searchWord ||
    formik.values.locationType !== appliedFilter.locationType ||
    formik.values.buildingId !== appliedFilter.buildingId ||
    formik.values.roomId !== appliedFilter.roomId ||
    formik.values.locationId !== appliedFilter.locationId

  const canCancel = !isScheduleFilterActive(searchParams)

  const handleCancel = React.useCallback(() => {
    setSearchParams((params) => {
      FILTER_SEARCH_PARAM_KEYS.forEach((key) => {
        params.delete(key)
      })
      return params
    })
    formik.resetForm({ values: DEFAULT_FILTER })
  }, [formik, setSearchParams])

  return (
    <fieldset disabled={formik.isSubmitting} className="flex w-full flex-col gap-5">
      <FormError />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <SelectInput
          label={t(['სასწავლო კვირა', 'Week'])}
          name="weekId"
          options={weekOptions}
          isLoading={isLearningWeeksLoading}
          menuPortalTarget={document.body}
        />
        <SelectInput
          label={t(['საათი', 'Hour'])}
          name="hourId"
          options={hourOptions}
          menuPortalTarget={document.body}
        />
        <SelectInput
          label={t(['ლექციის ტიპი', 'Lecture Type'])}
          name="lectureType"
          options={lectureTypeOptions}
          isLoading={isLectureTypesLoading}
          menuPortalTarget={document.body}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        <SelectInput
          label={t(['ლექციის ფორმატი', 'Lecture Format'])}
          name="lectureFormat"
          options={lectureFormatOptions}
          isLoading={isFormatsLoading}
          menuPortalTarget={document.body}
        />
        {showFilterLocationType && (
          <SelectInput
            label={t(['ლოკაციის ტიპი', 'Location Type'])}
            name="locationType"
            options={filterLocationTypeOptions}
            menuPortalTarget={document.body}
          />
        )}
        {showFilterBuildingRoom && (
          <>
            <SelectInput
              label={t(['კორპუსი', 'Building'])}
              name="buildingId"
              options={filterBuildingOptions}
              isLoading={isFilterBuildingsLoading}
              menuPortalTarget={document.body}
            />
            <SelectInput
              label={t('common:room')}
              name="roomId"
              options={filterRoomOptions}
              isLoading={isFilterRoomsLoading}
              disabled={formik.values.buildingId === ''}
              menuPortalTarget={document.body}
            />
          </>
        )}
        {showFilterOtherLocation && (
          <SelectInput
            label={t(['სხვა ლოკაცია', 'Other Location'])}
            name="locationId"
            options={filterLocationOptions}
            isLoading={isFilterLocationsLoading}
            menuPortalTarget={document.body}
          />
        )}
      </div>

      <TextInput
        type="text"
        label={t('common:additional_information')}
        name="searchWord"
        placeholder={t('common:additional_information')}
      />

      <div className="flex justify-end gap-2 border-t border-borderGrey pt-5">
        <Button variant="blue" disabled={formik.isSubmitting || !isDirty} type="submit">
          {t('common:search')}
        </Button>
        <Button variant="yellow" type="reset" disabled={formik.isSubmitting || canCancel} onClick={handleCancel}>
          {t('common:cancel')}
        </Button>
      </div>
    </fieldset>
  )
}

// ─── Schedule form (add / edit) ───────────────────────────────────────────────

type ScheduleHourSelectOption = {
  readonly label: string
  readonly value: string
  readonly options?: readonly { readonly label: string; readonly value: string }[]
}

function flattenScheduleHourSelectOptions(
  options: readonly ScheduleHourSelectOption[]
): Array<{ readonly label: string; readonly value: string }> {
  const flattened: Array<{ readonly label: string; readonly value: string }> = []
  for (const option of options) {
    if (option.options != null && option.options.length > 0) {
      flattened.push(...option.options)
    } else {
      flattened.push(option)
    }
  }
  return flattened
}

function ScheduleHourSelectInput({
  isEditMode,
  options,
  label,
  instructions,
}: {
  readonly isEditMode: boolean
  readonly options: readonly ScheduleHourSelectOption[]
  readonly label: string
  readonly instructions: React.ReactElement
}): JSX.Element {
  const t = useTranslatable()
  const [formData] = useFormData()
  const formik = useFormikContext<ScheduleFormValues>()
  const [field, meta, helpers] = useField<readonly string[]>({
    name: 'hourIds',
    validate(value) {
      if (value.length === 0) {
        return `${label} ${t('common:is_required')}`
      }
    },
  })
  const errors = formData.errors.fields.hourIds ?? []
  const isInvalid = (errors.length > 0 || meta.error != null) && meta.touched ? '!border-primaryRed' : ''

  if (!isEditMode) {
    return (
      <SelectInput
        label={label}
        name="hourIds"
        options={options}
        isMulti
        storeValueOnly
        closeMenuOnSelect={false}
        required
        menuPortalTarget={document.body}
        instructions={instructions}
      />
    )
  }

  const leafOptions = flattenScheduleHourSelectOptions(options)
  const selectedHourId = field.value[0] ?? ''

  return (
    <div className="mb-3 w-full">
      <div className="relative w-full" data-testid="hourIds">
        <label
          className="mb-[10px] flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor"
          htmlFor="hourIds"
        >
          {label}{' '}
          <span className="pl-1 text-lightSecondaryWarning" data-testid={`${label}.required`}>
            *
          </span>{' '}
          {instructions}
        </label>
        <Select
          placeholder={t('common:select')}
          className={`p-0 ${isInvalid}`}
          options={options}
          name={field.name}
          value={leafOptions.find((option) => option.value === selectedHourId) ?? null}
          onChange={(option) => {
            const value = (option as { value: string } | null)?.value
            void helpers.setValue(value != null && value !== '' ? [value] : [])
          }}
          onBlur={() => {
            void helpers.setTouched(true, true)
          }}
          instanceId="hourIds"
          hasError={meta.error != null && meta.touched && field.value.length === 0}
          inputId="hourIds"
          isDisabled={formik.isSubmitting}
          noOptionsMessage={() => t('common:no_options')}
          menuPortalTarget={document.body}
        />
      </div>
      {meta.error != null && meta.touched && field.value.length === 0 ? (
        <div className="mt-2 text-[13px] text-errorMessages">{meta.error}</div>
      ) : null}
      {errors.map((error, i) => (
        <div key={i} className="mt-2 text-[13px] text-errorMessages">
          {error}
        </div>
      ))}
    </div>
  )
}

interface ScheduleFormContentProps {
  readonly mode: 'add' | 'edit'
  readonly headers: AuthenticatedHeaders
  readonly hours: readonly Api.AdministratorCourseGroupScheduleHour[]
  readonly locale: string
  readonly lectureTypeOptions: readonly { value: string; label: string }[]
  readonly couId: number
  readonly groupId: number
  readonly semId: number
  readonly groupLecturers: readonly Api.AdministratorCourseGroupLecturer[]
  readonly duplicates?: readonly Api.AdministratorCourseGroupScheduleDuplicateItem[]
  readonly scheduleConflictsPending: boolean
  readonly canForceSubmit: boolean
  readonly isMainAdministrator: boolean
  readonly onClearScheduleConflicts: () => void
  readonly onCancel: () => void
}

function ScheduleFormContent({
  mode,
  headers,
  hours,
  locale,
  lectureTypeOptions,
  couId,
  groupId,
  semId,
  groupLecturers,
  duplicates,
  scheduleConflictsPending,
  canForceSubmit,
  onClearScheduleConflicts,
  onCancel,
  isMainAdministrator,
}: ScheduleFormContentProps): JSX.Element {
  const formik = useFormikContext<ScheduleFormValues>()
  const t = useTranslatable()
  const isEditMode = mode === 'edit'

  const showForceSubmit = isMainAdministrator && scheduleConflictsPending && canForceSubmit && !formik.values.massUpdate

  const prevRoomIdRef = React.useRef<string | undefined>(undefined)
  React.useEffect(() => {
    if (!scheduleConflictsPending) {
      prevRoomIdRef.current = formik.values.roomId
      return
    }

    const prevRoomId = prevRoomIdRef.current
    if (prevRoomId !== undefined && prevRoomId !== formik.values.roomId) {
      onClearScheduleConflicts()
    }
    prevRoomIdRef.current = formik.values.roomId
  }, [formik.values.roomId, scheduleConflictsPending, onClearScheduleConflicts])

  const { data: formatsData, isLoading: isFormatsLoading } = useApi({
    endpoint: Api.getAdministratorListsLectureFormats,
    params: React.useMemo(() => ({ headers }), [headers]),
    suspense: false,
  })

  const defaultLectureTypeId = React.useMemo(() => lectureTypeOptions[0]?.value ?? '', [lectureTypeOptions])
  const defaultLectureFormatId = React.useMemo(() => getFirstListItemId(formatsData?.data), [formatsData])

  const hourSelected = formik.values.hourIds.length > 0
  const locationType = formik.values.locationType
  const buildingId = formik.values.buildingId
  const lectureFormatId = formik.values.lectureFormatId

  const scheduleHourOptions = React.useMemo(
    () => buildScheduleHourSelectOptions(hours, locale, formik.values.hourIds),
    [hours, locale, formik.values.hourIds]
  )

  const hourById = React.useMemo(() => new Map(hours.map((hour) => [String(hour.id), hour])), [hours])

  React.useEffect(() => {
    const hourIds = formik.values.hourIds
    if (hourIds.length <= 1) return

    const selectedDays = hourIds.map((hourId) => hourById.get(hourId)?.day).filter((day): day is number => day != null)
    if (new Set(selectedDays).size <= 1) return

    const anchorDay = hourById.get(hourIds[hourIds.length - 1]!)?.day
    if (anchorDay == null) return

    const sameDayHourIds = hourIds.filter((hourId) => hourById.get(hourId)?.day === anchorDay)
    if (sameDayHourIds.length !== hourIds.length) {
      void formik.setFieldValue('hourIds', sameDayHourIds)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.hourIds, hourById])

  const prevHourIdsRef = React.useRef<readonly string[]>([])
  const isInitialHourIdsEffectRef = React.useRef(true)
  const appliedDefaultLearningWeeksRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (isInitialHourIdsEffectRef.current) {
      isInitialHourIdsEffectRef.current = false
      prevHourIdsRef.current = formik.values.hourIds
      return
    }

    const prev = prevHourIdsRef.current
    prevHourIdsRef.current = formik.values.hourIds

    if (isEditMode) return

    const prevKey = prev.join(',')
    const currentKey = formik.values.hourIds.join(',')
    if (prevKey !== currentKey) {
      appliedDefaultLearningWeeksRef.current = null
      if (prev.length > 0 && formik.values.hourIds.length > 0) {
        void formik.setFieldValue('learningWeekDates', [])
      }
    }
    if (prev.length > 0 && formik.values.hourIds.length === 0) {
      void formik.setValues({
        ...formik.values,
        learningWeekDates: [],
        lectureTypeId: defaultLectureTypeId,
        lectureFormatId: defaultLectureFormatId,
        locationType: '1',
        buildingId: '',
        roomId: '',
        locationId: '',
        lecturers: [],
        info: '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.hourIds, isEditMode])

  const prevBuildingIdRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (prevBuildingIdRef.current === null) {
      prevBuildingIdRef.current = buildingId
      return
    }
    if (prevBuildingIdRef.current !== buildingId) {
      prevBuildingIdRef.current = buildingId
      void formik.setFieldValue('roomId', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildingId])

  const learningWeeksHourId = parseLastSelectedHourId(formik.values.hourIds)

  const { data: datesData, isLoading: isDatesLoading } = useApi({
    endpoint: Api.getAdministratorListsLearningWeeks,
    params: React.useMemo(
      () => ({
        headers,
        query: { semId, couId, groupId, hourId: learningWeeksHourId! },
      }),
      [headers, semId, couId, groupId, learningWeeksHourId]
    ),
    shouldFetch: hourSelected && !isEditMode && learningWeeksHourId != null,
    suspense: false,
  })

  const formatKind = getLectureFormatKind(lectureFormatId)
  const showLocationType = formatKind !== 'online'
  const showBuildingRoom = showLocationType && locationType === '1'
  const showOtherLocation = showLocationType && locationType === '2'

  const prevFormatRef = React.useRef(lectureFormatId)
  React.useEffect(() => {
    if (!hourSelected || lectureFormatId === '') return
    const prev = prevFormatRef.current
    prevFormatRef.current = lectureFormatId
    if (prev === lectureFormatId) return

    if (formatKind === 'online') {
      void formik.setFieldValue('locationType', '')
      void formik.setFieldValue('buildingId', '')
      void formik.setFieldValue('roomId', '')
      void formik.setFieldValue('locationId', '')
      return
    }
    if (formatKind === 'hybrid') {
      void formik.setFieldValue('locationType', '')
      void formik.setFieldValue('buildingId', '')
      void formik.setFieldValue('roomId', '')
      void formik.setFieldValue('locationId', '')
      return
    }
    void formik.setFieldValue('locationType', '1')
    void formik.setFieldValue('buildingId', '')
    void formik.setFieldValue('roomId', '')
    void formik.setFieldValue('locationId', '')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formatKind, hourSelected, lectureFormatId])

  React.useEffect(() => {
    if (locationType === '1') return
    if (formik.values.buildingId !== '' || formik.values.roomId !== '') {
      void formik.setFieldValue('buildingId', '')
      void formik.setFieldValue('roomId', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationType])

  React.useEffect(() => {
    if (showOtherLocation) return
    if (formik.values.locationId !== '') {
      void formik.setFieldValue('locationId', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showOtherLocation])

  const { data: scheduleLocationsData, isLoading: isScheduleLocationsLoading } = useApi({
    endpoint: Api.getAdministratorListsScheduleLocations,
    params: React.useMemo(() => ({ headers }), [headers]),
    shouldFetch: hourSelected && showOtherLocation,
    suspense: false,
  })

  const { data: buildingsData, isLoading: isBuildingsLoading } = useApi({
    endpoint: Api.getAdministratorListsBuildings,
    params: React.useMemo(() => ({ headers }), [headers]),
    shouldFetch: hourSelected && showBuildingRoom,
    suspense: false,
  })

  const { data: roomsData, isLoading: isRoomsLoading } = useApi({
    endpoint: Api.getAdministratorListsRooms,
    params: React.useMemo(
      () => ({ headers, query: buildingId !== '' ? { buildingId: parseInt(buildingId, 10) } : {} }),
      [headers, buildingId]
    ),
    shouldFetch: hourSelected && showBuildingRoom && buildingId !== '',
    suspense: false,
  })

  const { data: lecturersData, isLoading: isLecturersLoading } = useApi({
    endpoint: Api.getAdministratorCourseGroupScheduleLecturers,
    params: React.useMemo(
      () => ({ headers, args: { couId, groupId }, query: { semId } }),
      [headers, couId, groupId, semId]
    ),
    suspense: false,
  })

  const resolvedLecturers = React.useMemo(() => {
    if (lecturersData != null && lecturersData.length > 0) {
      return lecturersData
    }
    return mapGroupLecturersToAvailable(groupLecturers)
  }, [lecturersData, groupLecturers])

  const groupLecturerDegreeByUserId = React.useMemo(() => {
    const map = new Map<string, string | null | undefined>()
    for (const lecturer of groupLecturers) {
      const userId = lecturer.userId ?? lecturer.id
      map.set(String(userId), lecturer.degree)
    }
    return map
  }, [groupLecturers])

  const learningWeekOptions = React.useMemo(
    () =>
      (datesData ?? []).map((d) => ({
        value: d.date,
        label: d.weekLabel !== '' ? d.weekLabel : d.label !== '' ? d.label : d.date,
      })),
    [datesData]
  )

  const learningWeekValues = React.useMemo(() => learningWeekOptions.map((o) => o.value), [learningWeekOptions])

  const allWeeksSelected =
    learningWeekValues.length > 0 && learningWeekValues.every((v) => formik.values.learningWeekDates.includes(v))

  React.useEffect(() => {
    if (isEditMode || !hourSelected || isDatesLoading) return

    if (learningWeekValues.length === 0) {
      appliedDefaultLearningWeeksRef.current = null
      return
    }

    const defaultWeeks = (datesData ?? []).filter((d) => !d.isExam).map((d) => d.date)
    const defaultKey = `${formik.values.hourIds.join(',')}:${defaultWeeks.join(',')}`
    if (appliedDefaultLearningWeeksRef.current === defaultKey) return

    appliedDefaultLearningWeeksRef.current = defaultKey
    void formik.setFieldValue('learningWeekDates', defaultWeeks)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, hourSelected, isDatesLoading, datesData, formik.values.hourIds])

  const formatOptions = React.useMemo(
    () => (formatsData?.data ?? []).map((f) => ({ value: String(f.id), label: f.name })),
    [formatsData]
  )

  const locationTypeOptions = React.useMemo(() => {
    const options = [
      { value: '1', label: t(['უნივერსიტეტი', 'University']) },
      { value: '2', label: t(['სხვა', 'Other']) },
    ]
    if (formatKind === 'hybrid') {
      return [{ value: '', label: t(['გთხოვთ აირჩიოთ', 'Please choose']) }, ...options]
    }
    return options
  }, [formatKind, t])

  const buildingOptions = React.useMemo(
    () => [
      { value: '', label: t(['გთხოვთ აირჩიოთ კორპუსი', 'Please Choose Building']) },
      ...(buildingsData?.data ?? []).map((b) => ({ value: String(b.id), label: b.name })),
    ],
    [buildingsData, t]
  )

  const roomOptions = React.useMemo(
    () =>
      buildingId === ''
        ? [{ value: '', label: t('common:select') }]
        : [
            { value: '', label: t('common:select') },
            ...(roomsData?.data ?? []).map((r) => ({ value: String(r.id), label: r.name })),
          ],
    [buildingId, roomsData, t]
  )

  const scheduleLocationOptions = React.useMemo(() => {
    const placeholder = { value: '', label: t(['გთხოვთ აირჩიოთ ლოკაცია', 'Please Choose Location']) }
    const fromApi = (scheduleLocationsData?.data ?? [])
      .filter((l) => l.id > 0)
      .map((l) => {
        const label = l.title.trim()
        return {
          value: String(l.id),
          label: label !== '' ? label : `${t(['ლოკაცია', 'Location'])} #${l.id}`,
        }
      })
    return [placeholder, ...fromApi]
  }, [scheduleLocationsData, t])

  const lecturerByUserId = React.useMemo(() => {
    const map = new Map<string, Api.AdministratorCourseGroupAvailableLecturer>()
    for (const l of resolvedLecturers) {
      map.set(l.userId, l)
    }
    return map
  }, [resolvedLecturers])

  const availableLecturerPickerOptions = React.useMemo(
    () =>
      resolvedLecturers
        .filter((l) => !formik.values.lecturers.some((sel) => sel.userId === l.userId))
        .map((l) => ({
          value: l.userId,
          label: formatAvailableLecturerPickerLabel(l),
        })),
    [formik.values.lecturers, resolvedLecturers]
  )

  const [lecturerPickerKey, setLecturerPickerKey] = React.useState(0)

  const handleSelectAllWeeksCheckbox = React.useCallback(
    (checked: boolean) => {
      void formik.setFieldValue('learningWeekDates', checked ? [...learningWeekValues] : [])
    },
    [formik, learningWeekValues]
  )

  const handleAddLecturer = React.useCallback(
    (userId: string) => {
      if (userId === '') return
      const lecturer = lecturerByUserId.get(userId)
      if (lecturer == null) return
      if (formik.values.lecturers.some((l) => l.userId === userId)) return
      void formik.setFieldValue('lecturers', [
        ...formik.values.lecturers,
        { userId: lecturer.userId, profileId: lecturer.profileId, isPaid: true },
      ])
      setLecturerPickerKey((k) => k + 1)
    },
    [formik, lecturerByUserId]
  )

  const handleRemoveLecturer = React.useCallback(
    (userId: string) => {
      void formik.setFieldValue(
        'lecturers',
        formik.values.lecturers.filter((l) => l.userId !== userId)
      )
    },
    [formik]
  )

  const handleToggleLecturerPaid = React.useCallback(
    (userId: string, isPaid: boolean) => {
      void formik.setFieldValue(
        'lecturers',
        formik.values.lecturers.map((l) => (l.userId === userId ? { ...l, isPaid } : l))
      )
    },
    [formik]
  )

  const handleForceSubmit = React.useCallback(async () => {
    await formik.setFieldValue('force', true, false)
    await formik.submitForm()
  }, [formik])

  return (
    <fieldset disabled={formik.isSubmitting} className="flex w-full flex-col gap-5">
      <ScheduleFormScrollToError />
      <FormError />
      {isEditMode && <ScheduleDuplicatesAlert duplicates={duplicates} />}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_1fr_auto] lg:items-start">
        <ScheduleHourSelectInput
          isEditMode={isEditMode}
          options={scheduleHourOptions}
          label={t(['ლექციის საათი', 'Lecture Hour'])}
          instructions={
            <PopoverComponent placement="right">
              <span className="text-primaryTextColor">
                {t(['საათი: რომელ საათზე უნდა დაიწყოს ლექცია', 'At Which hour lecture should start'])}
              </span>
            </PopoverComponent>
          }
        />
        <SelectInput
          label={t(['ლექციის ტიპი', 'Lecture Type'])}
          name="lectureTypeId"
          options={lectureTypeOptions}
          required
          menuPortalTarget={document.body}
          instructions={
            <PopoverComponent placement="right">
              <span className="text-primaryTextColor">
                {t(['ლექციის ტიპი: მაგ. სემინარი, ლექცია და სხვ.', 'Lecture type: either Seminar, Lecture and etc'])}
              </span>
            </PopoverComponent>
          }
        />
        <div className="flex flex-col gap-3 rounded-md border border-borderGrey/80 bg-[rgba(0,0,0,0.02)] px-4 py-3 dark:bg-[rgba(255,255,255,0.03)] lg:min-w-[220px]">
          <div className="flex items-center gap-1">
            <Checkbox name="isHalfHour" label={t(['ნახევარი საათის ცხრილი', 'Is Half Hour Schedule'])} />
            <PopoverComponent placement="right">
              <span className="text-primaryTextColor">
                {t([
                  'ნახევარი საათის ცხრილი: აღნიშნავს, ცხრილი ნახევარ საათიანია',
                  'Half Hour Schedule: Whether schedule is a half hour',
                ])}
              </span>
            </PopoverComponent>
          </div>
          <div className="flex items-center gap-1">
            <Checkbox name="isSplitLecture" label={t(['გაყოფილი ლექცია', 'Is Split Lecture'])} />
            <PopoverComponent placement="right">
              <span className="text-primaryTextColor">
                {t([
                  'გაყოფილი ჯგუფი: ლექცია განკუთვნილია გაყოფილი ჯგუფისთვის',
                  'Split Group: Whether schedule is for split group',
                ])}
              </span>
            </PopoverComponent>
          </div>
        </div>
      </div>

      {hourSelected && (
        <>
          {!isEditMode && (
            <>
              <SelectInput
                label={t(['სასწავლო/საგამოცდო კვირები', 'Learning Weeks'])}
                name="learningWeekDates"
                options={learningWeekOptions}
                isMulti
                storeValueOnly
                required
                closeMenuOnSelect={false}
                isLoading={isDatesLoading}
                menuPortalTarget={document.body}
              />
              <label className="-mt-2 mb-3 inline-flex cursor-pointer items-center gap-2 font-bold text-primaryTextColor">
                <input
                  type="checkbox"
                  checked={allWeeksSelected}
                  onChange={(e) => handleSelectAllWeeksCheckbox(e.target.checked)}
                  disabled={formik.isSubmitting || learningWeekValues.length === 0}
                  className="size-4 rounded border-borderGrey"
                />
                {t(['ყველა კვირის არჩევა', 'Select All Weeks'])}
              </label>
            </>
          )}

          <div className={classNames(GRID_TWO, 'items-start')}>
            <SelectInput
              label={t(['ლექციის ფორმატი', 'Lecture Format'])}
              name="lectureFormatId"
              options={formatOptions}
              required
              isLoading={isFormatsLoading}
              menuPortalTarget={document.body}
              instructions={
                <PopoverComponent placement="right">
                  <span className="text-primaryTextColor">
                    {t(['ლექციის ფორმატი: ლექციის ფორმატი', 'Lecture Format: format of lecture'])}
                  </span>
                </PopoverComponent>
              }
            />
            <div className="mb-3">
              <label className="mb-[10px] flex flex-nowrap items-center pl-0 font-bold text-primaryTextColor">
                {t(['ცხრილის ლექტორები', 'Schedule Lecturers'])}
                <PopoverComponent placement="right">
                  <span className="text-primaryTextColor">
                    {t([
                      'ცხრილის ლექტორები: შეგიძლიათ აირჩიოთ ერთი ან მეტი ლექტორი',
                      'Schedule lecturers: you can choose one or more lecturers',
                    ])}
                  </span>
                </PopoverComponent>
              </label>
              <Select
                key={lecturerPickerKey}
                placeholder={t(['აირჩიეთ ლექტორები', 'Choose Lecturers'])}
                options={availableLecturerPickerOptions}
                value={null}
                onChange={(option) => {
                  const userId = (option as { value: string } | null)?.value
                  if (userId != null && userId !== '') {
                    handleAddLecturer(userId)
                  }
                }}
                isLoading={isLecturersLoading}
                isDisabled={formik.isSubmitting}
                menuPortalTarget={document.body}
                isClearable
              />
              {formik.values.lecturers.length > 0 && (
                <ul className="mt-3 divide-y divide-borderGrey rounded-lg border border-borderGrey bg-primaryWhite">
                  {formik.values.lecturers.map((entry) => {
                    const lecturer = lecturerByUserId.get(entry.userId)
                    const displayName = lecturer != null ? formatAvailableLecturerName(lecturer) : entry.userId
                    const degreeLabel = formatAvailableLecturerDegree(
                      lecturer?.degree ?? groupLecturerDegreeByUserId.get(entry.userId)
                    )
                    return (
                      <li key={entry.userId} className="flex flex-wrap items-center justify-between gap-3 p-3">
                        <div className="min-w-0 flex-1">
                          <span className="block text-sm font-medium text-primaryTextColor">{displayName}</span>
                          {degreeLabel !== '' && (
                            <span className="mt-0.5 block text-xs text-captionColor">{degreeLabel}</span>
                          )}
                        </div>
                        <label className="inline-flex shrink-0 cursor-pointer items-center gap-2 text-sm text-primaryTextColor">
                          <input
                            type="checkbox"
                            checked={entry.isPaid}
                            onChange={(e) => handleToggleLecturerPaid(entry.userId, e.target.checked)}
                            disabled={formik.isSubmitting}
                            className="size-4 rounded border-borderGrey"
                          />
                          {t(['ანაზღაურებადი', 'Paid Lecture'])}
                        </label>
                        <button
                          type="button"
                          className="inline-flex shrink-0 items-center justify-center rounded-md border border-borderGrey p-2 text-captionColor transition-colors hover:border-primaryRed hover:bg-red-50 hover:text-primaryRed"
                          onClick={() => handleRemoveLecturer(entry.userId)}
                          aria-label={t('common:remove')}
                          disabled={formik.isSubmitting}
                        >
                          <FontAwesomeIcon icon={ProRegularSvgIcons.faTrashCan} className="size-4" />
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          </div>

          {showLocationType && (
            <>
              <SelectInput
                label={t(['ლოკაციის ტიპი', 'Location Type'])}
                name="locationType"
                options={locationTypeOptions}
                required
                menuPortalTarget={document.body}
                instructions={
                  <PopoverComponent placement="right">
                    <span className="text-primaryTextColor">
                      {t([
                        'ლოკაციის ტიპი: უნივერსიტეტი ან სხვა ლოკაცია',
                        'Location type, either university or other location',
                      ])}
                    </span>
                  </PopoverComponent>
                }
              />

              {showBuildingRoom && (
                <div className={classNames(GRID_TWO, 'mt-4')}>
                  <SelectInput
                    label={t(['კორპუსი', 'Building'])}
                    name="buildingId"
                    options={buildingOptions}
                    required
                    isLoading={isBuildingsLoading}
                    menuPortalTarget={document.body}
                    instructions={
                      <PopoverComponent placement="right">
                        <span className="text-primaryTextColor">
                          {t(['კორპუსი: ცხრილის კორპუსი', 'Building: Schedule building'])}
                        </span>
                      </PopoverComponent>
                    }
                  />
                  <SelectInput
                    label={t('common:room')}
                    name="roomId"
                    options={roomOptions}
                    required
                    isLoading={isRoomsLoading}
                    disabled={buildingId === ''}
                    menuPortalTarget={document.body}
                    instructions={
                      <PopoverComponent placement="right">
                        <span className="text-primaryTextColor">
                          {t(['აუდიტორია: ცხრილის აუდიტორია', 'Auditorium: Schedule auditorium'])}
                        </span>
                      </PopoverComponent>
                    }
                  />
                </div>
              )}

              {showOtherLocation && (
                <div className="mt-4">
                  <SelectInput
                    label={t(['სხვა ლოკაცია', 'Other Location'])}
                    name="locationId"
                    options={scheduleLocationOptions}
                    required
                    isLoading={isScheduleLocationsLoading}
                    menuPortalTarget={document.body}
                  />
                </div>
              )}
            </>
          )}

          <TextareaInput
            label={t('common:additional_information')}
            name="info"
            placeholder={t('common:additional_information')}
            rows={4}
            instructions={
              <PopoverComponent placement="right">
                <span className="text-primaryTextColor">
                  {t(['დამატებითი ინფორმაცია ცხრილის შესახებ', 'Additional Information regarding schedule'])}
                </span>
              </PopoverComponent>
            }
          />

          {isEditMode && (
            <div className="flex items-center gap-2">
              <Checkbox
                name="massUpdate"
                label={t([
                  'ყველა მსგავსი ცხრილის ჩანაწერის განახლება',
                  'Update All Schedules Matching Selected Schedule',
                ])}
                labelClassname="text-primaryRed"
              />
              <PopoverComponent placement="right">
                <span className="text-primaryTextColor">
                  {t([
                    'ჩანაწერები შეირჩევა საათისა და ლოკაციის დამთხვევით კურსისა და ჯგუფის ფარგლებში',
                    'Records will be selected by matching Hour and Location in the range of course and group!',
                  ])}
                </span>
              </PopoverComponent>
            </div>
          )}
        </>
      )}

      <div className="flex justify-end gap-2 border-t border-borderGrey pt-5">
        {showForceSubmit ? (
          <Button
            variant="yellow"
            type="button"
            disabled={formik.isSubmitting || !formik.dirty}
            onClick={handleForceSubmit}
          >
            {isEditMode
              ? t(['დუბლიკატური ჩანაწერით განახლება', 'Update with duplicate entry'])
              : t(['დუბლიკატური ჩანაწერით დამატება', 'Add with duplicate entry'])}
          </Button>
        ) : (
          <Button variant="blue" type="submit" disabled={formik.isSubmitting || !formik.dirty}>
            {isEditMode ? t(['ცხრილის განახლება', 'Update Schedule']) : t(['ცხრილის დამატება', 'Add Schedule'])}
          </Button>
        )}
        <Button
          variant="outline"
          type="button"
          disabled={formik.isSubmitting}
          className="!border-borderGrey !bg-hover-card !text-primaryTextColor hover:!bg-hover-card hover:!opacity-100"
          onClick={onCancel}
        >
          {t('common:close')}
        </Button>
      </div>
    </fieldset>
  )
}

// ─── Schedule card ────────────────────────────────────────────────────────────

const ScheduleCard = React.memo(function ScheduleCard({
  schedule,
  locale,
  lecturerPersonalNumberByUserId,
  weekLabelByDate,
  canModify,
  onEdit,
  onDelete,
}: {
  readonly schedule: Api.AdministratorCourseGroupSchedule
  readonly locale: string
  readonly lecturerPersonalNumberByUserId: ReadonlyMap<string, string>
  readonly weekLabelByDate: ReadonlyMap<string, string>
  readonly canModify: boolean
  readonly onEdit: (schedule: Api.AdministratorCourseGroupSchedule) => void
  readonly onDelete: (schedule: Api.AdministratorCourseGroupSchedule) => void
}): JSX.Element {
  const [expanded, setExpanded] = React.useState(false)
  const t = useTranslatable()
  const theme = useTheme()

  const formattedDate = React.useMemo(() => {
    if (!schedule.date) return ''
    const parts = schedule.date.split('-')
    const dateStr = parts.length === 3 ? `${parts[2]}-${parts[1]}-${parts[0]}` : schedule.date
    const weekLabel = weekLabelByDate.get(schedule.date)
    if (weekLabel == null) return dateStr
    const cleanedWeekLabel = weekLabel.replace(/\s*\(\d{2}-\d{2}-\d{4}\)/, '')
    return `${dateStr}(${cleanedWeekLabel})`
  }, [schedule.date, weekLabelByDate])

  const formattedHour = schedule.hour != null ? formatHour(schedule.hour, locale) : '—'

  const locationTypeLabel = React.useMemo(() => {
    if (schedule.locationType === 1) return t(['უნივერსიტეტი', 'University'])
    if (schedule.locationType === 2) return t(['სხვა', 'Other'])
    if (schedule.locationType == null) {
      const formatName = schedule.lectureFormat?.name.toLowerCase() ?? ''
      if (formatName.includes('online')) return t(['ონლაინ', 'Online'])
      return '—'
    }
    return String(schedule.locationType)
  }, [schedule.locationType, schedule.lectureFormat?.name, t])

  const hasDuplicates = schedule.duplicates != null && schedule.duplicates.length > 0
  const duplicateTooltipId = `schedule-duplicate-${schedule.id}`
  const duplicateTooltipText = React.useMemo(
    () => (schedule.duplicates ?? []).map((duplicate) => formatScheduleDuplicateMessage(duplicate, t)).join('\n'),
    [schedule.duplicates, t]
  )

  return (
    <>
      {hasDuplicates && (
        <Tooltip
          id={duplicateTooltipId}
          place="top"
          variant={theme === 'dark' ? 'dark' : 'light'}
          className="z-[99] max-w-[90%] whitespace-pre-line break-words"
        >
          {duplicateTooltipText}
        </Tooltip>
      )}
      <li
        data-tooltip-id={hasDuplicates ? duplicateTooltipId : undefined}
        className={classNames(
          'relative min-w-0 overflow-hidden rounded-card border px-6 py-5 transition-colors',
          hasDuplicates
            ? 'border-borderGrey bg-primaryRed/30 hover:!bg-primaryRed/45 dark:bg-primaryRed/25 dark:hover:!bg-primaryRed/40'
            : 'border-borderGrey bg-card hover:border-primaryBlue/40'
        )}
      >
        <div className="mb-4 flex items-center justify-end gap-1.5">
          {canModify && (
            <>
              <button
                type="button"
                disabled={!canModify}
                aria-label={t('common:edit')}
                onClick={() => onEdit(schedule)}
                className={classNames(
                  'inline-flex items-center justify-center rounded-md border border-borderGrey p-2.5 transition-colors',
                  canModify
                    ? 'text-captionColor hover:border-primaryBlue hover:bg-blue-50 hover:text-primaryBlue'
                    : 'cursor-not-allowed text-captionColor opacity-50'
                )}
              >
                <FontAwesomeIcon icon={ProRegularSvgIcons.faPencil} className="size-4" />
              </button>
              <button
                type="button"
                disabled={!canModify}
                aria-label={t('common:delete')}
                onClick={() => onDelete(schedule)}
                className={classNames(
                  'inline-flex items-center justify-center rounded-md border border-borderGrey p-2.5 transition-colors',
                  canModify
                    ? 'text-captionColor hover:border-primaryRed hover:bg-red-50 hover:text-primaryRed'
                    : 'cursor-not-allowed text-captionColor opacity-50'
                )}
              >
                <FontAwesomeIcon icon={ProRegularSvgIcons.faTrashCan} className="size-4" />
              </button>
            </>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            aria-label={expanded ? t(['ჩაკეცვა', 'Collapse']) : t(['გაშლა', 'Expand'])}
            className="inline-flex items-center justify-center rounded-md border border-borderGrey p-2.5 text-captionColor transition-colors hover:border-primaryBlue hover:bg-blue-50 hover:text-primaryBlue"
          >
            <FontAwesomeIcon
              icon={expanded ? ProRegularSvgIcons.faChevronUp : ProRegularSvgIcons.faChevronDown}
              className="size-4"
            />
          </button>
        </div>

        {schedule.lectureFormat?.id === 2 ? (
          <div className="grid min-w-0 grid-cols-2 gap-3">
            <ScheduleField label={t('common:date')} value={formattedDate} />
            <ScheduleField label={t(['საათი', 'Hour'])} value={formattedHour} />
          </div>
        ) : schedule.building?.name != null || schedule.room?.name != null ? (
          <div className="grid min-w-0 grid-cols-4 gap-3">
            <ScheduleField label={t('common:date')} value={formattedDate} />
            <ScheduleField label={t(['საათი', 'Hour'])} value={formattedHour} />
            <ScheduleField label={t(['კორპუსი', 'Building'])} value={schedule.building?.name ?? '—'} />
            <ScheduleField label={t('common:room')} value={schedule.room?.name ?? '—'} />
          </div>
        ) : schedule.location?.title != null ? (
          <div className="grid min-w-0 grid-cols-3 gap-3">
            <ScheduleField label={t('common:date')} value={formattedDate} />
            <ScheduleField label={t(['საათი', 'Hour'])} value={formattedHour} />
            <ScheduleField label={t(['ლოკაცია', 'Location'])} value={schedule.location.title} />
          </div>
        ) : (
          <div className="grid min-w-0 grid-cols-4 gap-3">
            <ScheduleField label={t('common:date')} value={formattedDate} />
            <ScheduleField label={t(['საათი', 'Hour'])} value={formattedHour} />
            <ScheduleField label={t(['კორპუსი', 'Building'])} value="—" />
            <ScheduleField label={t('common:room')} value="—" />
          </div>
        )}
        {(schedule.isSplitLecture || (schedule.lecturers != null && schedule.lecturers.length > 0)) && (
          <div className="mt-2 grid w-full min-w-0 grid-cols-2 gap-3">
            {schedule.isSplitLecture ? (
              <ScheduleField label={t(['ჯგუფის ტიპი', 'Group Type'])} value={t(['გაყოფილი ჯგუფი', 'Split Group'])} />
            ) : (
              <div />
            )}
            {schedule.lecturers != null && schedule.lecturers.length > 0 && (
              <ScheduleField
                label={t('lecturer:lecturers')}
                value={schedule.lecturers
                  .map((lecturer) => {
                    const personalNumber = lecturerPersonalNumberByUserId.get(String(lecturer.userId))
                    return personalNumber != null && personalNumber !== ''
                      ? `${personalNumber} - ${lecturer.name}`
                      : lecturer.name
                  })
                  .join(', ')}
              />
            )}
          </div>
        )}

        {schedule.info != null && schedule.info.trim() !== '' && (
          <div className="mt-4">
            <ScheduleTextBlock label={t('common:additional_information')} value={schedule.info} />
          </div>
        )}

        <div
          className={classNames(
            'grid transition-all duration-200 ease-in-out',
            expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
          )}
        >
          <div className="overflow-hidden">
            <div className="mt-2 border-t border-borderGrey pb-2 pt-4">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
                <ScheduleField label={t(['ლოკაციის ტიპი', 'Location Type'])} value={locationTypeLabel} />
                {schedule.lectureType != null && (
                  <ScheduleField label={t(['ლექციის ტიპი', 'Lecture Type'])} value={schedule.lectureType.name} />
                )}
                {schedule.lectureFormat != null && (
                  <ScheduleField label={t(['ლექციის ფორმატი', 'Lecture Format'])} value={schedule.lectureFormat.name} />
                )}
              </div>
            </div>
          </div>
        </div>
      </li>
    </>
  )
})

function ScheduleField({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <span className="mb-[10px] block font-bold text-primaryTextColor">{label}</span>
      <div
        title={value}
        className="w-full truncate rounded-[5px] border border-borderGrey bg-card px-[15px] py-[10.5px] text-sm leading-5 text-primaryTextColor"
      >
        {value !== '' ? value : ' '}
      </div>
    </div>
  )
}

function ScheduleTextBlock({ label, value }: { readonly label: string; readonly value: string }): JSX.Element {
  return (
    <div className="min-w-0">
      <span className="mb-3 block text-sm font-medium text-primaryTextColor">{label}</span>
      <div className="rounded-md border border-borderGrey bg-card px-3.5 py-3">
        <p className="whitespace-pre-wrap break-words text-base font-medium leading-relaxed text-primaryTextColor">
          {value}
        </p>
      </div>
    </div>
  )
}
